import { MepInsightsService } from "../../../../../../../../services/mep/mep-insights.service";
import { MepInsightsRequest, MepInsightsCorrelationWithPortCallsAndTime } from "../../../../../../../../models/mep/mep-insights.models";
import { BarChartViewModel, ChartItem, ChartBar } from "../../../../common-vm/bar-chart-view-model";
import * as Enumerable from "linq";
import { MepCorrelationTypeEnum } from "../../../../../../../../constants/mep/mep-insights.constants";
import {MepInsightsFilter} from "../mep-insights-filter/mep-insights-filter";
import {computedFrom, useView} from "aurelia-framework";

@useView('views/pages/modules/mep/tabs/mep-insights/partials/mep-correlation-with-port-time-calls/mep-insights-correlation-with-port-time-calls.html')
export class MepInsightsCorrelationWithPortTimeCalls {
    public isTypeSelected: boolean = true;
    public isCorrelationDataLoading: boolean;
    public correlationData: MepInsightsCorrelationWithPortCallsAndTime[];
    public chartViewModel: BarChartViewModel;
    public tugboatName: string = "Tugboat";
    isMepTrial: boolean = false;

    constructor(private mepInsightsService: MepInsightsService, private filterViewModel: MepInsightsFilter, isMepTrial: boolean) {
        this.isMepTrial = isMepTrial;
    }

    setIsTypeSelected(x: boolean) {
        this.isTypeSelected = x;
        this.initCorrelationChart();
    }

    getCorellationWithPortCallsAndTime(request: MepInsightsRequest): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            this.isCorrelationDataLoading = true;

            this.mepInsightsService.getMepInsightsCorrelationWithPortCallsAndTime(request).then(response => {
                this.correlationData = response.results;
                this.initCorrelationChart();
                this.isCorrelationDataLoading = false;
                resolve(null);
            }).catch(() => {
                this.isCorrelationDataLoading = false;
                reject("MEP Insights Vessel Emissions Correlation - Error retrieving the vessel emissions correlation data!");
            });
        });
    }

    initCorrelationChart() {
        if (!this.correlationData || this.correlationData.length == 0)
            return;

        const emissionsName = "Emissions";
        const portCallsName = "Port Calls";
        const operatingTimeName = "Operating Time";
        this.chartViewModel = null;
        const chartItems: ChartItem[] = [];
        let maxSizeValue = 0;
        let groupedMaxValues = [
            Enumerable.from(this.correlationData).max(x => x.percentageEmissionsTonnesByVesselType),
            Enumerable.from(this.correlationData).max(x => x.percentagePortTimeHoursByVesselType)
        ];
        const maxValuesTugboats = Enumerable.from(this.correlationData).where(x => x.vesselTypeName != this.tugboatName)
        if (!this.filterViewModel.isOnlyTugBoatVesselTypeSelected && maxValuesTugboats.count() > 0) {
            groupedMaxValues = [...groupedMaxValues, maxValuesTugboats.max(x => x.percentageVesselCallsyVesselType)]
        }
        
        if(groupedMaxValues.length > 0) {
            maxSizeValue = Enumerable.from(groupedMaxValues).max(x => x);
        }

        let chartData: MepInsightsCorrelationWithPortCallsAndTime[];
        
        if (this.isTypeSelected) {
            chartData = Enumerable.from(this.correlationData).where(x => x.vesselTypeName != this.tugboatName).toArray();
        }
        else {
            chartData = Enumerable.from(this.correlationData).where(x => x.vesselTypeName == this.tugboatName).toArray();
        }

        if(!chartData) {
            return;
        }

        chartData.forEach(x => {
            const bars: ChartBar[] = [
                {
                    id: MepCorrelationTypeEnum.Emissions,
                    barName: emissionsName,
                    size: x.percentageEmissionsTonnesByVesselType,
                    value: x.percentageEmissionsTonnesByVesselType,
                    color: '#1A8574'
                },
                {
                    id: MepCorrelationTypeEnum.PortCalls,
                    barName: portCallsName,
                    size: x.percentageVesselCallsyVesselType,
                    value: x.percentageVesselCallsyVesselType,
                    color: '#F7661F'
                },
                {
                    id: MepCorrelationTypeEnum.OperatingTime,
                    barName: operatingTimeName,
                    size: x.percentagePortTimeHoursByVesselType,
                    value: x.percentagePortTimeHoursByVesselType,
                    color: '#003745',
                }
            ];

            // if tug jobs is selected we don't show port calls
            if (!this.isTypeSelected) {
                bars.splice(1, 1);
            }

            const chartItem: ChartItem = {
                id: x.vesselTypeId,
                itemName: x.vesselTypeName,
                bars: bars
            }

            chartItems.push(chartItem);
        });

        //Sort by emissions
        chartItems.sort((a, b) => {
            return b.bars[0].size - a.bars[0].size;
        });

        const stepsForMaxValue = Math.ceil(maxSizeValue / 10);
        let numberOfSteps = Math.min(10, stepsForMaxValue);
        //if it's 1 then max value is 10 and client wants in that case have y axis split into 1, 2, 3... 10
        numberOfSteps = numberOfSteps == 1 ? 10 : numberOfSteps;
        this.chartViewModel = new BarChartViewModel(chartItems, stepsForMaxValue * 10, numberOfSteps);
    }
    
    @computedFrom("filterViewModel.operationModes", "filterViewModel.selectedOperationModes", "correlationData")
    get showTugBoatsSwitch (): boolean {
        const canShowTugBoatsSwitch = this.filterViewModel.operationModes
            .filter(x=> this.filterViewModel.selectedOperationModes.indexOf(x.id) > -1)
                .map(x=> x.name)
                    .indexOf('Manoeuvre') > -1 || this.filterViewModel.selectedOperationModes.length == 0 
                        && this.correlationData && this.correlationData.filter(x=> x.vesselTypeName == this.tugboatName).length >= 1;
        
        // If we can't show the tug boats switch, select Port calls switch
        if(!canShowTugBoatsSwitch) {
            this.setIsTypeSelected(true);
        } else if (this.filterViewModel.isOnlyTugBoatVesselTypeSelected){
            this.setIsTypeSelected(false);
        }
        
        return canShowTugBoatsSwitch && !this.filterViewModel.isOnlyTugBoatVesselTypeSelected;
    }

    clearCorrelationData() {
        this.correlationData = null;
        this.chartViewModel = null;
    }
}